import { Component, computed, effect, input, output, signal, WritableSignal, inject } from '@angular/core';
import {
  CdkDrag,
  CdkDragDrop,
  CdkDragHandle,
  CdkDropList,
  CdkDropListGroup,
  moveItemInArray,
  transferArrayItem,
} from '@angular/cdk/drag-drop';
import {
  SplitCategorySelectionObject,
  TransactionItem,
  TransactionItemError,
  TransactionItemFromAPI,
  TransactionItemWithTracking,
} from '../../../../../../_interfaces/transactions.interface';
import { TransactionSplitItemComponent } from '../split-transaction-item/transaction-split-item.component';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ConfirmationRemoveModalComponent } from '../../../../../../shared/components/modals/confirmation-remove-modal/confirmation-remove-modal.component';
import { CurrencyPipe } from '@angular/common';
import { convertAPIItemsToStore, generateItemId } from '../../_helpers/transactions-helpers';
import { EmptyIndicatorPipe } from '../../../../../../shared/pipes/empty-indicator.pipe';

@Component({
  selector: 'app-transaction-split-categories-edit',
  imports: [
    CdkDropList,
    CdkDrag,
    CdkDropListGroup,
    TransactionSplitItemComponent,
    CdkDragHandle,
    CurrencyPipe,
    EmptyIndicatorPipe,
  ],
  templateUrl: './transaction-split-categories-edit.component.html',
  styleUrl: '../../../../../../../static/styles/components/transaction-line-items.scss',
})
export class TransactionSplitCategoriesEditComponent {
  private readonly modalService = inject(NgbModal);

  // Inputs
  splitCategories = input<SplitCategorySelectionObject[]>([]);
  itemsErrors = input<TransactionItemError[]>([]); // Array of error objects from API
  savedItems = input<TransactionItemFromAPI[]>([]); // Items from API if the transaction is already split
  editMode = input<boolean>(false); // True when editing existing split items

  // Outputs
  splitItemsChange = output<TransactionItem[]>();
  categoryDeleted = output<number>(); // Emits category value to delete from parent
  clearAllErrors = output<void>(); // Emits when errors should be cleared (e.g. after drag)

  // Structure for submitting the data. This cannot change
  splitItems: TransactionItem[] = [];

  private readonly itemsStoreSignal: WritableSignal<Map<number, TransactionItemWithTracking[]>> = signal(new Map());

  // Store local modifications to errors (cleared fields)
  private readonly clearedErrorsSignal: WritableSignal<Map<string, Set<string>>> = signal(new Map()); // trackingId → Set of cleared field names

  // Track whether saved items have been initialized (prevents re-population after deletion)
  private savedItemsInitialized = false;

  // Computed signal that builds template data from categories and stored items
  splitItemsTemplateData = computed(() => {
    const categories = this.splitCategories();
    const itemsStore = this.itemsStoreSignal();

    return categories.map((cat) => {
      // Get items for this category from the store
      const items = itemsStore.get(cat.value) || [];
      const subtotal = items.reduce((sum, item) => {
        const itemAmount = item.amount ? Number.parseFloat(item.amount) : null;
        return itemAmount === null ? sum : sum + itemAmount;
      }, 0);

      return {
        category: cat,
        items: items,
        subtotal: subtotal,
      };
    });
  });

  // Computed signal that maps a flat API errors array to template-friendly structure
  itemErrorsByIndex = computed(() => {
    const errors = this.itemsErrors();
    const templateData = this.splitItemsTemplateData();
    const clearedErrors = this.clearedErrorsSignal();
    const errorMap = new Map<string, TransactionItemError>(); // Map of trackingId -> error object

    // Flatten all items to match the API order
    let flatIndex = 0;
    templateData.forEach((group) => {
      group.items.forEach((item) => {
        if (errors[flatIndex] && Object.keys(errors[flatIndex]).length > 0) {
          const itemErrors = { ...errors[flatIndex] };

          // Remove cleared field errors
          const clearedFields = clearedErrors.get(item._trackingId);
          if (clearedFields) {
            clearedFields.forEach((field) => {
              delete itemErrors[field as keyof TransactionItemError];
            });
          }

          // Only add to the map if there are still errors remaining
          if (Object.keys(itemErrors).length > 0) {
            errorMap.set(item._trackingId, itemErrors);
          }
        }
        flatIndex++;
      });
    });

    return errorMap;
  });

  // Computed signal that extracts category errors from the first item of each category
  categoryErrorsByValue = computed(() => {
    const errors = this.itemsErrors();
    const templateData = this.splitItemsTemplateData();
    const categoryErrorMap = new Map<number, string[]>(); // Map of category value -> category errors

    // Flatten all items to match the API order
    let flatIndex = 0;
    templateData.forEach((group) => {
      group.items.forEach(() => {
        // Check if this error object has category errors
        const categoryErrors = errors[flatIndex]?.category;
        if (categoryErrors && categoryErrors.length > 0) {
          // Store category errors by category value
          categoryErrorMap.set(group.category.value, categoryErrors);
        }
        flatIndex++;
      });
    });

    return categoryErrorMap;
  });

  constructor() {
    // Initialise new categories in the Map when they're added
    effect(() => {
      const categories = this.splitCategories();
      const currentStore = this.itemsStoreSignal();
      const editMode = this.editMode();

      // Track new categories before updating the store
      const newCategoriesWithoutItems: number[] = [];

      categories.forEach((cat) => {
        if (!currentStore.has(cat.value)) {
          newCategoriesWithoutItems.push(cat.value);
        }
      });

      if (newCategoriesWithoutItems.length > 0) {
        const newStore = new Map(currentStore);

        // Add new categories with first item automatically
        newCategoriesWithoutItems.forEach((categoryValue) => {
          // Auto-add first item to every new category
          // The only exception is when we're about to populate saved items
          // (savedItems.length > 0 AND not yet initialized)
          const savedItems = this.savedItems();
          const isLoadingSavedItems = editMode && savedItems.length > 0 && !this.savedItemsInitialized;

          if (!isLoadingSavedItems) {
            const trackingId = generateItemId();
            const newItem: TransactionItemWithTracking = {
              _trackingId: trackingId,
              category: categoryValue,
              name: '',
              unit_price: undefined,
              unit_quantity: undefined,
              amount: null,
              tax: undefined,
              tracking: [],
            };
            newStore.set(categoryValue, [newItem]);
          } else {
            // About to load saved items, just add empty array for now
            newStore.set(categoryValue, []);
          }
        });

        // Remove categories that no longer exist
        const categoryValues = new Set(categories.map((c) => c.value));
        newStore.forEach((_, key) => {
          if (!categoryValues.has(key)) {
            newStore.delete(key);
          }
        });

        this.itemsStoreSignal.set(newStore);

        // Emit changes if items were added
        if (!editMode || this.savedItemsInitialized) {
          this.emitSplitItems();
        }
      } else {
        // Check if we need to remove any categories
        const categoryValues = new Set(categories.map((c) => c.value));
        let needsRemoval = false;

        currentStore.forEach((_, key) => {
          if (!categoryValues.has(key)) {
            needsRemoval = true;
          }
        });

        if (needsRemoval) {
          const newStore = new Map(currentStore);
          newStore.forEach((_, key) => {
            if (!categoryValues.has(key)) {
              newStore.delete(key);
            }
          });
          this.itemsStoreSignal.set(newStore);
        }
      }
    });

    // Effect to reset cleared errors when new errors come from API
    effect(() => {
      const errors = this.itemsErrors();

      // If new errors are received, reset the cleared errors tracking
      if (errors && errors.length > 0) {
        this.clearedErrorsSignal.set(new Map());
      }
    });

    // Effect to populate saved items when entering edit mode
    effect(() => {
      const savedItems = this.savedItems();
      const editMode = this.editMode();
      const categories = this.splitCategories();

      if (editMode && savedItems.length > 0 && categories.length > 0) {
        this.populateSavedItems(savedItems);
      }
    });
  }

  // Populate items from saved data (edit mode)
  private populateSavedItems(savedItems: TransactionItemFromAPI[]) {
    // Check if we've already initialized saved items
    // This prevents re-population when user deletes all items
    if (this.savedItemsInitialized) {
      return;
    }

    // Use the helper function to convert API items to store format
    const newStore = convertAPIItemsToStore(savedItems);

    // Update the store
    this.itemsStoreSignal.set(newStore);
    this.emitSplitItems();

    // Mark as initialized to prevent re-population
    this.savedItemsInitialized = true;
  }

  // Add a new item to a specific category
  addNewItem(categoryValue: number) {
    const currentStore = this.itemsStoreSignal();
    const newStore = new Map(currentStore);

    const trackingId = generateItemId(); // Use helper function
    const newItem: TransactionItemWithTracking = {
      _trackingId: trackingId,
      category: categoryValue,
      name: '',
      unit_price: undefined,
      unit_quantity: undefined,
      amount: null,
      tax: undefined,
      vat: undefined,
      tracking: [],
    };

    const existingItems = newStore.get(categoryValue) || [];

    // Add new item
    newStore.set(categoryValue, [...existingItems, newItem]);

    // Update the signal with a new Map
    this.itemsStoreSignal.set(newStore);
    this.emitSplitItems();

    // Clear all validation errors when adding a new item
    this.clearAllErrorsLocal();
  }

  // Handle item updates from a child component
  onItemChange(updatedItem: TransactionItem, categoryValue: number, itemIndex: number) {
    const currentStore = this.itemsStoreSignal();
    const newStore = new Map(currentStore);

    const items = newStore.get(categoryValue) || [];
    const updatedItems = items.map((item, index) => {
      if (index === itemIndex) {
        // Preserve _trackingId when updating!
        return {
          ...updatedItem,
          _trackingId: item._trackingId, // Keep the original tracking ID
        } as TransactionItemWithTracking;
      }
      return item;
    });

    newStore.set(categoryValue, updatedItems);
    this.itemsStoreSignal.set(newStore);
    this.emitSplitItems();
  }

  // Handle item deletion
  onDeleteItem(itemIndex: number, categoryValue: number) {
    const currentStore = this.itemsStoreSignal();
    const newStore = new Map(currentStore);

    const items = newStore.get(categoryValue) || [];
    const filteredItems = items.filter((_, index) => index !== itemIndex);

    newStore.set(categoryValue, filteredItems);
    this.itemsStoreSignal.set(newStore);
    this.emitSplitItems();

    // Clear all validation errors when deleting an item
    this.clearAllErrorsLocal();
  }

  // Handle clearing a specific field error when the user starts typing
  onClearFieldError(trackingId: string, field: string) {
    const currentClearedErrors = this.clearedErrorsSignal();
    const newClearedErrors = new Map(currentClearedErrors);

    const clearedFields = newClearedErrors.get(trackingId) || new Set<string>();
    clearedFields.add(field);
    newClearedErrors.set(trackingId, clearedFields);

    this.clearedErrorsSignal.set(newClearedErrors);
  }

  // Helper method to clear all validation errors
  private clearAllErrorsLocal() {
    this.clearedErrorsSignal.set(new Map());
    this.clearAllErrors.emit();
  }

  // Handle category deletion with confirmation
  onDeleteCategory(category: SplitCategorySelectionObject) {
    const modalRef = this.modalService.open(ConfirmationRemoveModalComponent, {
      centered: true,
    });

    // Set modal data
    modalRef.componentInstance.subject = category.name;
    modalRef.componentInstance.removeMessage = `The items within the category will be removed from the split transaction data.`;

    // Handle confirmation
    modalRef.closed.subscribe({
      next: (confirmed: boolean) => {
        if (confirmed) {
          // Remove category from the items store
          const currentStore = this.itemsStoreSignal();
          const newStore = new Map(currentStore);
          newStore.delete(category.value);
          this.itemsStoreSignal.set(newStore);

          // Emit to parent to remove from splitCategories
          this.categoryDeleted.emit(category.value);

          this.emitSplitItems();

          // Clear all validation errors when deleting a category
          this.clearAllErrorsLocal();
        }
      },
    });
  }

  // Flatten template data to TransactionItem[] for API submission
  emitSplitItems() {
    const flatArray: TransactionItem[] = [];
    const itemsStore = this.itemsStoreSignal();

    // Flatten all items from the Map and strip tracking ID
    itemsStore.forEach((items) => {
      items.forEach((item) => {
        // Remove _trackingId before sending to parent/API
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { _trackingId, ...cleanItem } = item;

        flatArray.push(cleanItem as TransactionItem);
      });
    });

    this.splitItems = flatArray;
    this.splitItemsChange.emit(flatArray);
  }

  // PHASE 2: Drag & Drop handler for line items
  drop(event: CdkDragDrop<TransactionItemWithTracking[]>, targetCategoryValue: number) {
    // Clear all validation errors when any drag & drop occurs
    this.clearAllErrorsLocal();

    const currentStore = this.itemsStoreSignal();
    const newStore = new Map(currentStore);

    if (event.previousContainer === event.container) {
      // Reordering items within the SAME category
      const items = newStore.get(targetCategoryValue) || [];
      moveItemInArray(items, event.previousIndex, event.currentIndex);
      newStore.set(targetCategoryValue, [...items]); // Create a new array reference
    } else {
      // Moving item to a DIFFERENT category
      const previousCategoryValue = Number.parseInt(event.previousContainer.id.replace('category-', ''));
      const sourceItems = newStore.get(previousCategoryValue) || [];
      const targetItems = newStore.get(targetCategoryValue) || [];

      // Get the item being moved
      const movedItem = sourceItems[event.previousIndex];

      // Update the item's category field (preserve _trackingId)
      movedItem.category = targetCategoryValue;

      transferArrayItem(sourceItems, targetItems, event.previousIndex, event.currentIndex);

      // Update both categories with new array references
      newStore.set(previousCategoryValue, [...sourceItems]);
      newStore.set(targetCategoryValue, [...targetItems]);
    }

    // Update store and emit changes
    this.itemsStoreSignal.set(newStore);
    this.emitSplitItems();
  }
}
